import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Icon, X } from '../../ui/Icon';
import { FIELD_LABEL } from './documentFiles';

interface Props {
  value: number | null;
  onChange: (minutes: number | null) => void;
  /** Shows the "needs a start time" hint — a reminder can't fire without one. */
  warn?: boolean;
}

/** Reminder lead time, stored as minutes but edited as a number + unit. */
export function ReminderField({ value, onChange, warn = false }: Props) {
  const { t } = useTranslation();

  const display = (v: number | null) =>
    !v ? { num: '', unit: 'minutes' as const }
       : v >= 60 && v % 60 === 0
         ? { num: String(v / 60), unit: 'hours' as const }
         : { num: String(v), unit: 'minutes' as const };

  const [num, setNum] = useState(() => display(value).num);
  const [unit, setUnit] = useState<'minutes' | 'hours'>(() => display(value).unit);

  // The parent can swap `value` under us — the flight form refills every stop
  // when an extraction returns, and the multi-flight flow reuses these instances
  // across steps. Only re-derive on a change we didn't cause: resyncing on our
  // own commits would rewrite "60 minutes" as "1 hour" while the user types.
  const lastEmitted = useRef(value);
  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    const d = display(value);
    setNum(d.num);
    setUnit(d.unit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function commit(n: string, u: typeof unit) {
    const parsed = parseInt(n, 10);
    const next = !n || isNaN(parsed) || parsed <= 0 ? null : u === 'hours' ? parsed * 60 : parsed;
    lastEmitted.current = next;
    onChange(next);
  }

  return (
    <div>
      <label className={FIELD_LABEL}>
        <span className="flex items-center gap-1.5">
          <Icon as={Bell} size={12} />
          {t('bookings.reminder.label')}
        </span>
      </label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="1"
          max={unit === 'hours' ? 24 : 1440}
          placeholder="—"
          className="input w-24 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          value={num}
          onChange={e => { setNum(e.target.value); commit(e.target.value, unit); }}
        />
        <select
          className="input flex-1"
          value={unit}
          onChange={e => {
            const u = e.target.value as typeof unit;
            setUnit(u);
            commit(num, u);
          }}
        >
          <option value="minutes">{t('bookings.reminder.minutes')}</option>
          <option value="hours">{t('bookings.reminder.hours')}</option>
        </select>
        {value && (
          <button
            type="button"
            onClick={() => { setNum(''); onChange(null); }}
            className="w-8 h-8 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center transition-colors cursor-pointer shrink-0"
            aria-label={t('common.delete')}
          >
            <Icon as={X} size={14} />
          </button>
        )}
      </div>
      {value && warn && (
        <p className="text-[10px] text-amber-500 dark:text-amber-400 mt-1.5 flex items-center gap-1">
          <Icon as={Bell} size={12} />
          {t('bookings.reminder.warnNoTime')}
        </p>
      )}
    </div>
  );
}
